import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { financeApi, type FinanceDetail as Detail } from "../../api/finance";
import type { ApiError } from "../../api/client";
import { FinanceDetail } from "./FinanceDetail";
import { FinanceFilters, type FiltersValue } from "./FinanceFilters";
import { presetRange } from "./usePeriod";

export default function FinanceCompanyPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const init = useMemo<FiltersValue>(() => ({ preset: "ytd", ...presetRange("ytd") }), []);
  const [filters, setFilters] = useState<FiltersValue>(init);
  // Sporna potraživanja se podrazumevano ne prikazuju i ne ulaze u saldo, dospelo i dospelost.
  const [sporna, setSporna] = useState(false);

  useEffect(() => {
    if (!id) return;
    setData(null);
    financeApi.company(id, { from: filters.from, to: filters.to, sporna })
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, [id, filters.from, filters.to, sporna]);

  // After a dispute mark the card is read again in place: the card and the open invoice window stay as they are.
  const osvezi = () => {
    if (!id) return;
    financeApi.company(id, { from: filters.from, to: filters.to, sporna })
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  };

  const onChange = (v: FiltersValue) => {
    const range = v.preset === "custom" ? { from: v.from, to: v.to } : presetRange(v.preset);
    setFilters({ ...v, ...range });
  };

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/finance" style={{ color: "var(--ink-3)", textDecoration: "none" }}>{t("finance.title", "Finance")}</Link></span>
            <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{data?.company.name ?? "…"}</span>
          </nav>
          <h1>{data?.company.name ?? t("finance.title", "Finance")}</h1>
        </div>
        <div className="page-actions">
          <FinanceFilters value={filters} markets={[]} countries={[]} onChange={onChange}
                          sporna={sporna} onSpornaChange={setSporna} />
        </div>
      </div>
      {error && <div className="empty">{error}</div>}
      {data
        ? <FinanceDetail data={data} onDisputesChanged={osvezi} />
        : <p style={{ color: "var(--ink-3)", padding: 20 }}>Loading…</p>}
    </div>
  );
}
